import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { BcRestApi } from 'common/api/rest-api';
import { SmartResponseModel } from './smart-response-model';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { broadcastEvents } from 'common/common/constants/broadcast-events';
import { ChatBase } from 'common/xislivechat/models/chat-base';

@Component({
  selector: 'app-smart-responses',
  templateUrl: './smart-responses.component.html',
  styleUrls: ['./smart-responses.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class SmartResponsesComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  isSmartResponsesActive: boolean = false;

  @Input()
  record: ChatBase;

  @Input()
  hasDiscussionsFeature: boolean = false;

  @Output()
  public setFocusOnEditor: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public smartResponseSelected: EventEmitter<SmartResponseModel> = new EventEmitter<SmartResponseModel>();

  @Output()
  public smartResponsesClosed: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('smartResponseCloseMessage', { static: true })
  private _smartResponseCloseMessage: ElementRef;

  @ViewChildren('smartResponseListElement')
  private _smartResponseListElement: QueryList<any>;

  public isSmartResponseLoading = false;
  public noSmartResponses = true;
  public closeMessageUIElement: HTMLElement;
  public smartResponses: SmartResponseModel[];
  public smartResponseHasFocus: boolean;

  private _rxEmitterSubscriptionFocusSmartResponses;

  constructor(public bcRestApi: BcRestApi,
              private bcAnalytics: BcAnalytics,
              private rxEmitter: RxEmitter,
              private changeDetector: ChangeDetectorRef) {
  }

  ngOnInit() {
    this._rxEmitterSubscriptionFocusSmartResponses = this.rxEmitter.subscribe(broadcastEvents.focusSmartResponses, () => this._initSmartResponses());
  }

  ngAfterViewInit() {
    this.closeMessageUIElement = this._smartResponseCloseMessage.nativeElement;
  }

  ngOnDestroy() {
    if (this._rxEmitterSubscriptionFocusSmartResponses) {
      this._rxEmitterSubscriptionFocusSmartResponses.unsubscribe();
      this._rxEmitterSubscriptionFocusSmartResponses = null;
    }
  }

  handleKeyDownCloseMessage(event): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.which === KeyboardKeyCodes.ESC || ((event.metaKey || event.ctrlKey) && event.which === KeyboardKeyCodes.SPACE)) {
      this._closeSmartResponsesAndTrack();
    }
  }

  onClick(smartResponse: SmartResponseModel, event): void {
    event.preventDefault();
    event.stopPropagation();

    this._clearSmartResponseSelections();
    this._smartResponseSelectedCallback(smartResponse);
    this._closeSmartResponsesAndFocusEditor();
  }

  onMouseDown(event): void {
    event.preventDefault();
    event.stopPropagation();

    this.smartResponseHasFocus = true;
  }

  handleKeyDown(smartResponse: SmartResponseModel, event: KeyboardEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.which === KeyboardKeyCodes.DOWN) {
      if (index < this.smartResponses.length - 1) {
        this._changeSmartResponseFocus(smartResponse, index + 1);
      }
    } else if (event.which === KeyboardKeyCodes.UP) {
      if (index > 0) {
        this._changeSmartResponseFocus(smartResponse, index - 1);
      }
    } else if (event.which === KeyboardKeyCodes.ENTER) {
      this._smartResponseSelectedCallback(smartResponse);
      this._closeSmartResponsesAndFocusEditor();
    } else if (event.which === KeyboardKeyCodes.ESC || ((event.metaKey || event.ctrlKey) && event.which === KeyboardKeyCodes.SPACE)) {
      this._closeSmartResponsesAndTrack();
    }
  }

  onBlur(): void {
    this.smartResponseHasFocus = false;
    // The timeout is needed here because this logic needs to run after the onFocus event.
    // This is needed to handle a click outside of the Smart Response panel.
    setTimeout(() => {
      if (!this.smartResponseHasFocus) {
        this._closeSmartResponsesAndFocusEditor();
      }
    });
  }

  onFocus(): void {
    this.smartResponseHasFocus = true;
  }

  private _clearSmartResponseSelections(): void {
    for (let idx = 0; idx < this.smartResponses.length; idx++) {
      this.smartResponses[idx].selected = false;
    }
  }

  private _initSmartResponses(): void {
    const numberOfSmartResponses = 4;
    this.isSmartResponseLoading = true;
    this.bcRestApi.getSmartResponses(this.record)
      .then((smartResponses) => {
        if (smartResponses.length > 0) {
          this.smartResponses = smartResponses.slice(0, numberOfSmartResponses);
          this._clearSmartResponseSelections();
          this._changeSmartResponseFocus(this.smartResponses[0], 0);
          this.noSmartResponses = false;
        } else {
          this.noSmartResponses = true;
          setTimeout(() => {
            this.closeMessageUIElement.focus();
          });
        }
        this.isSmartResponseLoading = false;
      });
    this.bcAnalytics.eventTrack('SmartResponsesOpened');
  }

  private _smartResponseSelectedCallback(smartResponse: SmartResponseModel): void {
    smartResponse.selected = true;
    this.smartResponseSelected.emit(smartResponse);
    const index = this.smartResponses.findIndex(response => response.selected === true);
    this.bcAnalytics.eventTrack('SmartResponseSelected', { index: index + 1 });
  }

  private _changeSmartResponseFocus(currentSmartResponse: SmartResponseModel, newIndex: number): void {
    // noinspection Eslint
    currentSmartResponse.selected = false;
    const sm = this.smartResponses[newIndex];
    sm.selected = true;
    this.changeDetector.detectChanges();
    const selectedSmartResponseElement = this._smartResponseListElement.find((item, index: number) => index === newIndex);
    selectedSmartResponseElement.nativeElement.focus();
  }

  private _closeSmartResponsesAndFocusEditor(): void {
    this.smartResponsesClosed.emit();
    this.setFocusOnEditor.emit();
    setTimeout(() => {
      this.smartResponses = [];
    }, 500);
  }

  private _closeSmartResponsesAndTrack(): void {
    this._closeSmartResponsesAndFocusEditor();
    this.bcAnalytics.eventTrack('SmartResponseClosed');
  }
}
